import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Star, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { adminApi, fmtErr } from "../api";
import { useAuth } from "../AuthContext";
import { Btn, ConfirmDialog, Modal, Field, inputCls, MediaPicker, Thumb } from "../components";

const blank = { name: "", logo_url: "", website: "", order: 0, active: true, featured: false };

const ClientsAdmin = () => {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null); // {id?, ...form}
  const [picker, setPicker] = useState(false);
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => adminApi.get("/clients").then((r) => setItems(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...edit, order: Number(edit.order) || 0 };
      if (edit.id) await adminApi.put(`/clients/${edit.id}`, payload);
      else await adminApi.post("/clients", payload);
      toast.success(edit.id ? "Client updated" : "Client added");
      setEdit(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  const toggle = async (c, key) => {
    try {
      await adminApi.put(`/clients/${c.id}`, {
        name: c.name, logo_url: c.logo_url, website: c.website, order: c.order,
        active: key === "active" ? !c.active : c.active,
        featured: key === "featured" ? !c.featured : c.featured,
      });
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await adminApi.delete(`/clients/${del.id}`);
      toast.success("Client deleted");
      setDel(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-clients-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Major Clients</h1>
          <p className="mt-1 text-sm text-slate-500">Active clients appear in the public Major Clients section. Inactive clients are hidden.</p>
        </div>
        <Btn onClick={() => setEdit({ ...blank })} data-testid="add-client-btn"><Plus size={14} /> Add Client</Btn>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              {["Logo", "Name", "Order", "Featured", "Status", "Actions"].map((h) => (
                <th key={h} className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items === null ? (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No clients added yet. Add your first approved client.</td></tr>
            ) : (
              items.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`client-row-${c.id}`}>
                  <td className="px-5 py-3"><Thumb src={c.logo_url} alt={c.name} /></td>
                  <td className="px-5 py-3 font-semibold text-slate-900">
                    {c.name}
                    {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="ml-2 inline-block text-slate-400 hover:text-[#C5221F]"><ExternalLink size={12} /></a>}
                  </td>
                  <td className="px-5 py-3 text-slate-600">{c.order}</td>
                  <td className="px-5 py-3">
                    <button onClick={() => toggle(c, "featured")} className={`p-1.5 ${c.featured ? "text-amber-500" : "text-slate-300 hover:text-amber-400"}`} data-testid={`client-featured-${c.id}`}>
                      <Star size={16} fill={c.featured ? "currentColor" : "none"} />
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <button onClick={() => toggle(c, "active")} className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 border rounded-sm ${c.active ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-200 text-slate-500 border-slate-300"}`} data-testid={`client-active-${c.id}`}>
                      {c.active ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => setEdit({ ...c })} className="p-2 text-slate-500 hover:text-[#C5221F]" data-testid={`client-edit-${c.id}`}><Pencil size={15} /></button>
                      {user.role === "super_admin" && (
                        <button onClick={() => setDel(c)} className="p-2 text-slate-400 hover:text-red-600" data-testid={`client-delete-${c.id}`}><Trash2 size={15} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? "Edit Client" : "Add Client"}>
        {edit && (
          <form onSubmit={save} className="space-y-4">
            <Field label="Client / Company Name *">
              <input className={inputCls} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} required minLength={2} data-testid="client-name-input" />
            </Field>
            <Field label="Logo">
              <div className="flex items-center gap-3">
                {edit.logo_url ? <img src={edit.logo_url} alt="" className="h-14 w-auto object-contain border border-slate-200 p-1" /> : <Thumb src="" />}
                <Btn variant="ghost" type="button" onClick={() => setPicker(true)} data-testid="client-logo-btn">{edit.logo_url ? "Replace Logo" : "Upload Logo"}</Btn>
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Website URL (optional)">
                <input className={inputCls} value={edit.website} onChange={(e) => setEdit({ ...edit, website: e.target.value })} placeholder="https://…" data-testid="client-website-input" />
              </Field>
              <Field label="Display Order">
                <input type="number" className={inputCls} value={edit.order} onChange={(e) => setEdit({ ...edit, order: e.target.value })} data-testid="client-order-input" />
              </Field>
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} className="w-4 h-4 accent-[#C5221F]" data-testid="client-active-check" /> Active (visible publicly)
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={edit.featured} onChange={(e) => setEdit({ ...edit, featured: e.target.checked })} className="w-4 h-4 accent-[#C5221F]" data-testid="client-featured-check" /> Featured
              </label>
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
              <Btn variant="ghost" type="button" onClick={() => setEdit(null)}>Cancel</Btn>
              <Btn type="submit" disabled={busy} data-testid="client-save-btn">{busy && <Loader2 size={13} className="animate-spin" />} Save Client</Btn>
            </div>
          </form>
        )}
      </Modal>

      <MediaPicker open={picker} onClose={() => setPicker(false)} onSelect={(url) => setEdit({ ...edit, logo_url: url })} />
      <ConfirmDialog open={!!del} title="Delete this client?" message={`"${del?.name}" will be permanently removed.`} onConfirm={doDelete} onClose={() => setDel(null)} busy={busy} />
    </div>
  );
};

export default ClientsAdmin;
